
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, Alert } from 'reactstrap';
import ExpenseForm from './ExpenseForm';
import ExpenseList from './ExpenseList';
import SupplierStatements from './SupplierStatements';
import ReverseBankLinkModal from './ReverseBankLinkModal';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

const ExpensesManager = () => {
    const [activeTab, setActiveTab] = useState('1'); // '1' = Historial, '2' = Estados de Cuenta
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [todayStats, setTodayStats] = useState({ total: 0, bancolombia: 0, mercadopago: 0, caja_menor: 0, total_month: 0 });

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Reverse Link Modal
    const [reverseLinkOpen, setReverseLinkOpen] = useState(false);
    const [linkingExpense, setLinkingExpense] = useState(null);

    // Filter State (Global for this module)
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        siigo_status: '',
        payment_status: ''
    });

    // Detail Modal State
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categoryExpenses, setCategoryExpenses] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchCategoryDetails = async (category) => {
        try {
            setSelectedCategory(category);
            setLoadingDetails(true);
            setDetailModalOpen(true);

            const response = await api.get('/expenses', {
                params: {
                    month: filters.month,
                    year: filters.year,
                    category: category
                }
            });

            if (response.data.success) {
                // Sort by amount descending (highest to lowest) as requested
                const sortedData = response.data.data.sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
                setCategoryExpenses(sortedData);
            }
        } catch (error) {
            console.error("Error fetching category details:", error);
            toast.error("Error al cargar detalles");
        } finally {
            setLoadingDetails(false);
        }
    };

    const toggleDetailModal = () => {
        setDetailModalOpen(!detailModalOpen);
    };

    const toggleModal = (mode = null) => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setEditingExpense(null); // Reset on close
        } else if (mode) {
            // If opening, set initial data with mode
            setEditingExpense({ mode });
        }
    };

    const handleEdit = (expense) => {
        setEditingExpense({ ...expense, mode: expense.payment_status === 'PAGADO' ? 'payment' : 'schedule' });
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este registro?')) {
            try {
                await api.delete(`/expenses/${id}`);
                toast.success('Gasto eliminado');
                setRefreshTrigger(prev => prev + 1);
            } catch (error) {
                console.error(error);
                toast.error('Error al eliminar');
            }
        }
    };

    const handleSuccess = () => {
        setRefreshTrigger(prev => prev + 1);
        fetchDailyStats();
        toggleModal(); // Close modal
    };

    // === SIIGO BULK SYNC ===
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [syncModalOpen, setSyncModalOpen] = useState(false);

    const handleSiigoSync = async () => {
        if (!window.confirm(`¿Importar todas las facturas de compra de Siigo del mes ${monthNames[parseInt(filters.month)]} ${filters.year}?\n\nSe crearán como egresos PENDIENTES de pago.`)) {
            return;
        }

        setSyncing(true);
        try {
            const response = await api.post('/expenses/siigo-import', {
                month: filters.month,
                year: filters.year
            });

            if (response.data.success) {
                setSyncResult(response.data);
                setSyncModalOpen(true);
                toast.success(response.data.message);
                setRefreshTrigger(prev => prev + 1);
                fetchDailyStats();
            } else {
                toast.error(response.data.message || 'Error en sincronización');
            }
        } catch (error) {
            console.error('Error syncing Siigo:', error);
            toast.error(error.response?.data?.message || 'Error consultando Siigo');
        } finally {
            setSyncing(false);
        }
    };

    const fetchDailyStats = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const response = await api.get('/expenses/stats', {
                params: {
                    // date: today, // Let backend decide based on Bogota time, as ISOString is UTC and shifts date at 7PM
                    month: filters.month,
                    year: filters.year
                }
            });
            if (response.data.success) {
                console.log('📊 Stats Received:', response.data.data);
                setTodayStats(response.data.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchDailyStats();
    }, [refreshTrigger, filters]);

    const currencyFormatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
    });

    const monthNames = [
        "", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    return (
        <div className="content">
            <Nav tabs className="mb-4 mt-2 border-0">
                <NavItem>
                    <NavLink
                        className={classnames('font-weight-bold', { 'active font-weight-bolder text-primary': activeTab === '1' })}
                        onClick={() => { setActiveTab('1'); }}
                        style={{cursor: 'pointer', borderTopLeftRadius: '0.375rem', borderTopRightRadius: '0.375rem', backgroundColor: activeTab === '1' ? '#fff' : '#f8f9fe', border: '1px solid #e9ecef', borderBottomColor: activeTab === '1' ? '#fff' : '#e9ecef'}}
                    >
                        <i className="ni ni-bullet-list-67 mr-2"></i> Historial de Compras (Gral)
                    </NavLink>
                </NavItem>
                <NavItem className="ml-2">
                    <NavLink
                        className={classnames('font-weight-bold', { 'active font-weight-bolder text-primary': activeTab === '2' })}
                        onClick={() => { setActiveTab('2'); }}
                        style={{cursor: 'pointer', borderTopLeftRadius: '0.375rem', borderTopRightRadius: '0.375rem', backgroundColor: activeTab === '2' ? '#fff' : '#f8f9fe', border: '1px solid #e9ecef', borderBottomColor: activeTab === '2' ? '#fff' : '#e9ecef'}}
                    >
                        <i className="ni ni-chart-bar-32 mr-2"></i> Estado de Cuentas (Proveedores)
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
            {/* Stats Row */}
            <Row className="mb-4">
                <Col lg="3" md="6">
                    <Card className="card-stats mb-4 mb-xl-0 shadow h-100">
                        <CardBody>
                            <Row>
                                <div className="col">
                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                        Total Mes ({monthNames[parseInt(filters.month)]})
                                    </CardTitle>
                                    <span className="h2 font-weight-bold mb-0">
                                        {currencyFormatter.format(todayStats.total_month || 0)}
                                    </span>
                                </div>
                                <Col className="col-auto">
                                    <div className="icon icon-shape bg-primary text-white rounded-circle shadow">
                                        <i className="ni ni-calendar-grid-58" />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="3" md="6">
                    <Card className="card-stats mb-4 mb-xl-0 shadow h-100">
                        <CardBody>
                            <Row>
                                <div className="col">
                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                        Total Egresos Hoy
                                    </CardTitle>
                                    <span className="h2 font-weight-bold mb-0">
                                        {currencyFormatter.format(todayStats.total)}
                                    </span>
                                </div>
                                <Col className="col-auto">
                                    <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                                        <i className="ni ni-money-coins" />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="4">
                    <Card className="card-stats mb-4 mb-xl-0 shadow h-100">
                        <CardBody>
                            <Row>
                                <div className="col">
                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                        Bancolombia
                                    </CardTitle>
                                    <span className="h2 font-weight-bold mb-0">
                                        {currencyFormatter.format(todayStats.bancolombia)}
                                    </span>
                                </div>
                                <Col className="col-auto">
                                    <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                                        <i className="ni ni-briefcase-24" />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="4">
                    <Card className="card-stats mb-4 mb-xl-0 shadow h-100">
                        <CardBody>
                            <Row>
                                <div className="col">
                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                        Mercado Pago
                                    </CardTitle>
                                    <span className="h2 font-weight-bold mb-0">
                                        {currencyFormatter.format(todayStats.mercadopago)}
                                    </span>
                                </div>
                                <Col className="col-auto">
                                    <div className="icon icon-shape bg-info text-white rounded-circle shadow">
                                        <i className="ni ni-credit-card" />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="4">
                    <Card className="card-stats mb-4 mb-xl-0 shadow h-100">
                        <CardBody>
                            <Row>
                                <div className="col">
                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                        Caja Menor
                                    </CardTitle>
                                    <span className="h2 font-weight-bold mb-0">
                                        {currencyFormatter.format(todayStats.caja_menor)}
                                    </span>
                                </div>
                                <Col className="col-auto">
                                    <div className="icon icon-shape bg-success text-white rounded-circle shadow">
                                        <i className="ni ni-shop" />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* NEW GROUPED CATEGORY STATS */}
            {todayStats.groups && (
                <>
                    {/* MERCHANDISE SECTION */}
                    <Row className="mb-2 mt-4">
                        <Col xs="12">
                            <h6 className="heading-small text-muted mb-3 text-uppercase font-weight-bold">
                                🛒 COMPRAS DE MERCANCÍA (MES) - TOTAL: <span className="text-primary">{currencyFormatter.format(todayStats.groups.merchandise.total)}</span>
                            </h6>
                        </Col>
                        {todayStats.groups.merchandise.items.length > 0 ? (
                            todayStats.groups.merchandise.items.map((cat, index) => (
                                <Col key={'merc_' + index} lg="2" md="4" className="mb-3">
                                    <Card
                                        className="card-stats shadow h-100 border-0 cursor-pointer element-hover"
                                        style={{ borderLeft: '4px solid #5e72e4', cursor: 'pointer', transition: 'all 0.2s' }}
                                        onClick={() => fetchCategoryDetails(cat.category)}
                                    >
                                        <CardBody className="p-3">
                                            <Row>
                                                <div className="col">
                                                    <CardTitle tag="h6" className="text-uppercase text-muted mb-1 text-xs">{cat.category}</CardTitle>
                                                    <span className="h4 font-weight-bold mb-0 text-dark">{currencyFormatter.format(cat.total)}</span>
                                                </div>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <Col className="mb-3"><span className="text-muted text-sm pl-3">Sin movimientos de mercancía este mes.</span></Col>
                        )}
                    </Row>

                    {/* OPERATIONAL SECTION */}
                    <Row className="mb-4">
                        <Col xs="12">
                            <h6 className="heading-small text-muted mb-3 text-uppercase font-weight-bold">
                                🏢 GASTOS OPERATIVOS Y OTROS (MES) - TOTAL: <span className="text-primary">{currencyFormatter.format(todayStats.groups.operational.total)}</span>
                            </h6>
                        </Col>
                        {todayStats.groups.operational.items.length > 0 ? (
                            todayStats.groups.operational.items.map((cat, index) => (
                                <Col key={'op_' + index} lg="2" md="4" className="mb-3">
                                    <Card
                                        className="card-stats shadow h-100 border-0 cursor-pointer element-hover"
                                        style={{ borderLeft: '4px solid #11cdef', cursor: 'pointer', transition: 'all 0.2s' }}
                                        onClick={() => fetchCategoryDetails(cat.category)}
                                    >
                                        <CardBody className="p-3">
                                            <Row>
                                                <div className="col">
                                                    <CardTitle tag="h6" className="text-uppercase text-muted mb-1 text-xs">{cat.category}</CardTitle>
                                                    <span className="h4 font-weight-bold mb-0 text-dark">{currencyFormatter.format(cat.total)}</span>
                                                </div>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <Col className="mb-3"><span className="text-muted text-sm pl-3">Sin gastos operativos este mes.</span></Col>
                        )}
                    </Row>
                </>
            )}

            {/* FALLBACK FOR LEGACY / IF GROUPS MISSING */}
            {!todayStats.groups && todayStats.by_category && todayStats.by_category.length > 0 && (
                <Row className="mb-4">
                    <Col xs="12">
                        <h6 className="heading-small text-muted mb-3">ACUMULADO POR TIPO DE GASTO (Mes)</h6>
                    </Col>
                    {todayStats.by_category.map((cat, index) => (
                        <Col key={index} lg="2" md="4" className="mb-3">
                            <Card className="card-stats shadow h-100 border-0">
                                <CardBody className="p-3">
                                    <Row>
                                        <div className="col">
                                            <CardTitle tag="h6" className="text-uppercase text-muted mb-1 text-xs">
                                                {cat.category}
                                            </CardTitle>
                                            <span className="h4 font-weight-bold mb-0 text-dark">
                                                {currencyFormatter.format(cat.total)}
                                            </span>
                                        </div>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Row className="mb-3">
                <Col className="text-right d-flex justify-content-end align-items-center">
                    <Button
                        color="info"
                        outline
                        onClick={handleSiigoSync}
                        disabled={syncing}
                        className="mr-3"
                    >
                        {syncing ? (
                            <><Loader2 size={16} className="mr-1" style={{ animation: 'spin 1s linear infinite' }} /> Importando...</>
                        ) : (
                            <><i className="ni ni-cloud-download-95 mr-1" /> Sincronizar FCs de Siigo</>
                        )}
                    </Button>
                    <Button color="success" onClick={() => toggleModal('payment')} className="mr-3">
                        <i className="ni ni-check-bold" /> Registrar Pago Realizado
                    </Button>
                    <Button color="warning" onClick={() => toggleModal('schedule')}>
                        <i className="ni ni-time-alarm" /> Programar Pago
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col xs="12">
                    <ExpenseList
                        refreshTrigger={refreshTrigger}
                        filters={filters}
                        setFilters={setFilters}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onLinkBank={(expense) => {
                            setLinkingExpense(expense);
                            setReverseLinkOpen(true);
                        }}
                    />
                </Col>
            </Row>

            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={() => toggleModal()}>
                    {editingExpense?.id ? 'Editar Gasto' : (editingExpense?.mode === 'payment' ? 'Registrar Pago Realizado' : 'Programar Pago')}
                </ModalHeader>
                <ModalBody>
                    <ExpenseForm
                        key={editingExpense ? (editingExpense.id || editingExpense.mode) : 'new'}
                        onSuccess={handleSuccess}
                        onCancel={toggleModal}
                        initialData={editingExpense}
                    />
                </ModalBody>
            </Modal>

            {/* DETAIL MODAL */}
            <Modal isOpen={detailModalOpen} toggle={toggleDetailModal} size="lg">
                <ModalHeader toggle={toggleDetailModal}>
                    Detalle: {selectedCategory} ({monthNames[parseInt(filters.month)]})
                </ModalHeader>
                <ModalBody>
                    {loadingDetails ? (
                        <div className="text-center p-5">Loading...</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-items-center table-flush">
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col">Fecha</th>
                                        <th scope="col">Descripción</th>
                                        <th scope="col">Fuente</th>
                                        <th scope="col" className="text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categoryExpenses.length > 0 ? (
                                        categoryExpenses.map((expense, i) => (
                                            <tr key={i}>
                                                <td>
                                                    {(() => {
                                                        const dateToShow = expense.payment_date || expense.date;
                                                        if (!dateToShow) return <span className="text-muted">-</span>;
                                                        const d = new Date(dateToShow);
                                                        // Validar fecha efectiva
                                                        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
                                                    })()}
                                                </td>
                                                <td>
                                                    <span className="font-weight-bold">{expense.description}</span>
                                                    <div className="small text-muted">{expense.provider_name}</div>
                                                </td>
                                                <td>
                                                    {expense.source === 'bancolombia' && <span className="badge badge-warning">Bancolombia</span>}
                                                    {expense.source === 'mercadopago' && <span className="badge badge-info">Mercado Pago</span>}
                                                    {expense.source === 'caja_menor' && <span className="badge badge-success">Caja Menor</span>}
                                                </td>
                                                <td className="text-right font-weight-bold">
                                                    {currencyFormatter.format(expense.amount)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center">No se encontraron registros.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* SYNC RESULTS MODAL */}
            <Modal isOpen={syncModalOpen} toggle={() => setSyncModalOpen(false)} size="md">
                <ModalHeader toggle={() => setSyncModalOpen(false)}>
                    Resultado de Sincronización Siigo
                </ModalHeader>
                <ModalBody>
                    {syncResult && (
                        <div>
                            <div className="d-flex justify-content-around mb-4">
                                <div className="text-center">
                                    <h2 className="text-success mb-0">{syncResult.data?.imported || 0}</h2>
                                    <small className="text-muted">Importadas</small>
                                </div>
                                <div className="text-center">
                                    <h2 className="text-warning mb-0">{syncResult.data?.skipped || 0}</h2>
                                    <small className="text-muted">Ya existían</small>
                                </div>
                            </div>

                            {syncResult.data?.imported_list?.length > 0 && (
                                <div>
                                    <h6 className="text-success">Nuevas facturas importadas:</h6>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
                                            <thead>
                                                <tr>
                                                    <th># FC</th>
                                                    <th>Proveedor</th>
                                                    <th className="text-right">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {syncResult.data.imported_list.map((item, i) => (
                                                    <tr key={i}>
                                                        <td className="font-weight-bold">{item.fc}</td>
                                                        <td>{item.supplier}</td>
                                                        <td className="text-right">{currencyFormatter.format(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <Alert color="info" className="mt-3 mb-0">
                                <small>
                                    <strong>Siguiente paso:</strong> Las facturas importadas aparecen como PENDIENTES.
                                    Edita cada una para registrar el pago (fuente de dinero + evidencia).
                                </small>
                            </Alert>
                        </div>
                    )}
                </ModalBody>
            </Modal>
                </TabPane>
                <TabPane tabId="2">
                    <SupplierStatements 
                        onPay={(expense) => handleEdit(expense)} 
                        onLinkBank={(expense) => {
                            setLinkingExpense(expense);
                            setReverseLinkOpen(true);
                        }}
                    />
                </TabPane>
            </TabContent>

            {/* Reverse Link Modal */}
            <ReverseBankLinkModal
                isOpen={reverseLinkOpen}
                toggle={() => { setReverseLinkOpen(false); setLinkingExpense(null); }}
                selectedExpense={linkingExpense}
                onSuccess={() => {
                    setRefreshTrigger(prev => prev + 1);
                }}
            />
        </div>
    );
};

export default ExpensesManager;
